import { Component, OnInit, inject, input, output } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatDialog } from '@angular/material/dialog';

import { map } from 'rxjs/operators';

import { Observable, combineLatest, of } from 'rxjs';

import { Document, FileMetadata, FilesDisplayComponent } from '@camelot/files-extended';
import {
  BottomSheetData,
  BottomSheetTemplateBasicComponent,
  BottomSheetTemplateBasicParams,
  FilterHelper,
  Menu,
} from '@camelot/menu';
import { LayoutFullPanelComponent, openModal } from '@camelot/ui';
import { CamBaseComponent, FileData, ModalState, downloadFile, getFileExtension } from '@camelot/utils';

import { CamTranslationCore } from '../../translation.service';
import { UploadDocumentModal, UploadDocumentResult } from './upload/upload-visit-document/upload-document.component';

@Component({
  selector: 'cam-documents',
  templateUrl: './documents.component.html',
  styleUrls: ['./documents.component.scss'],
  imports: [FilesDisplayComponent, LayoutFullPanelComponent, UploadDocumentModal],
})
export class DocumentsComponent extends CamBaseComponent implements OnInit {
  document$ = input.required<Observable<Document[]>>();

  canAddFile = input.required<boolean>();

  filesSaved = output<{ file: File; fileMetadata: Partial<FileMetadata> }>();

  filesDeleted = output<FileData>();

  private _bottomSheet = inject(MatBottomSheet);
  private _dialog = inject(MatDialog);

  public menu: Menu;
  public uploadModal = new ModalState<null, UploadDocumentResult>();

  public filterHelper = new FilterHelper([
    { defaultOpen: false, label: 'core.documents.mine' },
    { defaultOpen: true, label: 'core.documents.all' },
  ]);

  private _pendingFile: File | null = null;

  constructor() {
    super();
    CamTranslationCore.getInstance();
    this.menu = this.filterHelper.getMenu();
  }
  ngOnInit(): void {
    this._registerSubscription(
      combineLatest({
        all: this._filteredDocuments$('all'),
        mine: this._filteredDocuments$('mine'),
      }).subscribe(data => {
        this._updateMenu({ all: data.all.length, mine: data.mine.length });
      })
    );
  }

  public uploadDocuments(files: { file: File | null; localUrl: string | null }[]) {
    this._openUploadWorkflow(files[0].file!);
  }
  public downloadDocument(fileData: FileData) {
    downloadFile(fileData.url);
    this._bottomSheet.dismiss(true);
  }

  public openBottomSheet(fileData: FileData): void {
    const bottomSheetData: BottomSheetData[] = [
      {
        action: () => this.downloadDocument(fileData),
        icon: 'download',
        label: 'core.documents.download',
      },
      {
        action: () => this._proposeDeleteDocument(fileData),
        icon: 'delete',
        label: 'core.documents.delete',
      },
    ];

    this._bottomSheet.open<BottomSheetTemplateBasicComponent, BottomSheetTemplateBasicParams>(
      BottomSheetTemplateBasicComponent,
      {
        data: {
          menu$: of(bottomSheetData),
          orientation: 'horizontal',
        },
      }
    );
  }

  public getDocs$() {
    return this._filteredDocuments$(this.filterHelper.filter).pipe(
      map(documents =>
        documents.map<FileData>(document => {
          return {
            fileExtension: getFileExtension(document.url),
            fileMetaData: document.fileMetadata,
            id: document.id,
            type: 'Document',
            url: document.url,
          };
        })
      )
    );
  }

  public onUploadSaved(result: UploadDocumentResult) {
    if (this._pendingFile) {
      this._saveDocument(this._pendingFile, {
        description: result.description,
        fileType: {
          id: result.documentTypeId,
          translatedValue: '',
        },
      });
      this._pendingFile = null;
    }
  }

  private _filteredDocuments$(filter: string | null) {
    return this.document$().pipe(
      map(documents =>
        documents.filter(document => {
          if (filter === 'all') {
            return true;
          }
          if (filter === 'mine' && document.isOwner) {
            return true;
          }
          return false;
        })
      )
    );
  }

  private _saveDocument(file: File, fileMetadata: Partial<FileMetadata>) {
    this.requestState.asked();
    this.filesSaved.emit({ file, fileMetadata });
  }

  private _openUploadWorkflow(file: File) {
    this._pendingFile = file;
    this.uploadModal.asked(null);
  }

  private _proposeDeleteDocument(fileData: FileData) {
    this._registerSubscription(
      openModal(this._dialog).subscribe((result: any) => {
        if (result) {
          this._deleteDocument(fileData);
        }
      })
    );

    this._bottomSheet.dismiss(true);
  }

  private _deleteDocument(fileData: FileData) {
    this.filesDeleted.emit(fileData);
  }

  private _updateMenu(data: { all: number; mine: number }) {
    this.filterHelper.updateMenuDatas([
      {
        key: 'core.documents.mine',
        options: {
          notificationBadge: {
            label: data.mine,
          },
        },
      },
      {
        key: 'core.documents.all',
        options: {
          notificationBadge: {
            label: data.all,
          },
        },
      },
    ]);
    this.menu = this.filterHelper.getMenu();
  }
}
